import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Popover,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  InputLabel
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
 
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Formik, Form, FieldArray } from 'formik';
import DeleteField from '../../otherComponent/DeleteStep';
import { optionFor, useDetectionTargets } from './shared/detectionTargets';

interface CameraComponentProps {
  values: any;
  index: number;
  setFieldValue: (key: string, value: any) => void;
  selectedNodeData?: any;
  onDeleteField?: (fieldIndex: number) => void;
  toSentenceCase: (str: string) => string;
  fieldTypeSelector?: ReactNode;
  isSettingsOpen?: boolean;
  onSettingsOpen?: (fieldIndex: number) => void;
  onSettingsClose?: () => void;
}

export default function CameraComponent({ values, index, setFieldValue, selectedNodeData, onDeleteField, toSentenceCase, fieldTypeSelector, isSettingsOpen, onSettingsOpen, onSettingsClose }: CameraComponentProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // Detectable classes come from the server, so a new one needs no deploy.
  const detectionTargets = useDetectionTargets();
  const settingsButtonRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof isSettingsOpen !== 'boolean') {
      return;
    }

    if (!isSettingsOpen) {
      setAnchorEl(null);
      return;
    }

    if (settingsButtonRef.current) {
      setAnchorEl(settingsButtonRef.current);
    }
  }, [isSettingsOpen]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    onSettingsOpen?.(index);
  };

  const handleClose = () => {
    setAnchorEl(null);
    onSettingsClose?.();
  };

  const open = Boolean(anchorEl);
  const id = open ? 'edit-camera-popover' : undefined;

  const field = values.fields[index];
  const settings = field.settings || {};
  const label = field.label || field.question || 'Camera';

  const labelFields = [
    { label: 'Label', name: 'label', type: 'text' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container alignItems="center" spacing={0.5}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: 'calc(100% - 40px)',
                py: 1,
                px: 1,
                border: '1px solid #ccc',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: 0
              }}
            >
              <Typography
                variant="body1"
                sx={{ minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {toSentenceCase(label)}
              </Typography>
              <CameraAltIcon fontSize="small" sx={{ color: 'gray' }} />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box ref={settingsButtonRef} sx={{ cursor: 'pointer', color: 'primary.main' }} onClick={handleOpen}>
                <SettingsIcon fontSize="medium" />
              </Box>
              <DeleteField 
                onDeleteField={() => onDeleteField?.(index)}
                selectedNodeData={selectedNodeData}
                fieldIndex={index}
                fieldData={values.fields[index]}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ 
          sx: { 
            width: 350, 
            p: 2, 
            maxHeight: '60vh', 
            overflowY: 'auto',
            overflowX: 'hidden'
          } 
        }}
      >
        <Formik
          initialValues={{
            label: field.label || field.question || '',
            required: settings.required || false,
            imageInstructions:
              field.children && field.children.length > 0
                ? field.children
                : [{ title: '', description: '' }],
            cameraFacing: settings.cameraFacing || 'env',
            cameraMode: settings.cameraMode || 'IMAGE',
            minDuration: settings.minDuration || 0,
            detection: settings.detection || null
          }}
          enableReinitialize
          onSubmit={(formValues) => {
            const updatedField = {
              ...field,
              label: formValues.label,
              question: formValues.label,
              settings: {
                ...settings,
                required: formValues.required,
                cameraFacing: formValues.cameraFacing,
                cameraMode: formValues.cameraMode,
                minDuration: formValues.minDuration,
                detection: formValues.detection
              },
              children: formValues.imageInstructions
            };
            setFieldValue(`fields.${index}`, updatedField);
            handleClose();
          }}
        >
          {({ values: formValues, handleChange, handleBlur, setFieldValue }) => (
            <Form>
              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2} gap={1}>
                <Typography variant="subtitle1">Camera Settings</Typography>
                {fieldTypeSelector ? (
                  <Box sx={{ minWidth: 120 }}>
                    {fieldTypeSelector}
                  </Box>
                ) : null}
              </Box>

              <Grid container spacing={1}>
                {/* Label + Min Duration */}
                {labelFields.map(({ label, name, type }) => (
                  <Grid item xs={12} key={name} sx={{ mt: name === 'label' ? 0 : 1 }}> 
                    <TextField
                      fullWidth
                      label={label}
                      name={name}
                      type={type}
                      value={formValues[name as keyof typeof formValues]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      size="small"
                    />
                  </Grid>
                ))}

                <Grid item xs={12}>
                  <InputLabel sx={{ mb: 0.5 }}>Camera Facing</InputLabel>
                  <Select
                    fullWidth
                    name="cameraFacing"
                    value={formValues.cameraFacing}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="small"
                  >
                    <MenuItem value="env">Back</MenuItem>
                    <MenuItem value="front">Front</MenuItem>
                  </Select>
                </Grid>

                <Grid item xs={12}>
                  <InputLabel sx={{ mb: 0.5 }}>Camera Mode</InputLabel>
                  <Select
                    fullWidth
                    name="cameraMode"
                    value={formValues.cameraMode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="small"
                  >
                    <MenuItem value="IMAGE">IMAGE</MenuItem>
                    <MenuItem value="VIDEO">VIDEO</MenuItem>
                  </Select>
                </Grid>

                {formValues.cameraMode === 'VIDEO' && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Min Duration"
                      name="minDuration"
                      type="number"
                      value={formValues.minDuration}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      size="small"
                    />
                  </Grid>
                )}

                {formValues.cameraMode === 'IMAGE' && (
                  <Grid item xs={12}>
                    <FieldArray name="imageInstructions">
                      {({ push, remove }) => (
                        <Box>
                          {formValues.imageInstructions.map((img: any, idx: number) => (
                            <Accordion key={idx} sx={{ mb: 1 }} defaultExpanded={idx === 0}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  flexDirection: 'row',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  '&.Mui-expanded': {
                                    minHeight: '48px'
                                  },
                                  '& .MuiAccordionSummary-content.Mui-expanded': {
                                    margin: 0
                                  },
                                  '& .MuiAccordionSummary-content': {
                                    marginRight: 'auto'
                                  }
                                }}
                              >
                                <Typography fontWeight="bold">
                                  Image {idx + 1}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <TextField
                                      fullWidth
                                      label="Title"
                                      name={`imageInstructions.${idx}.title`}
                                      value={img.title}
                                      onChange={handleChange}
                                      size="small"
                                    />
                                  </Grid>
                                  <Grid item xs={12} mt={1}>
                                    <TextField
                                      fullWidth
                                      label="Description"
                                      name={`imageInstructions.${idx}.description`}
                                      value={img.description}
                                      onChange={handleChange}
                                      size="small"
                                    />
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end">
                                      <IconButton
                                        onClick={() => remove(idx)}
                                        color="error"
                                        disabled={formValues.imageInstructions.length === 1}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          ))}

                          <Box display="flex" justifyContent="flex-end" mt={1}>
                            <Button
                              variant="outlined"
                               
                              onClick={() => push({ title: '', description: '' })}
                              size="small"
                            >
                              Add Image Instruction
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </FieldArray>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="required"
                        checked={formValues.required}
                        onChange={handleChange}
                      />
                    }
                    label="Required"
                    sx={{ ml: -1.5, mt: -0.5 }}
                  />
                </Grid>

                {/* On-device document detection — guided auto-capture that only
                    accepts the expected document and blocks non-matching ones.
                    Applies to image capture (not video). */}
                {formValues.cameraMode !== 'VIDEO' && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!formValues.detection}
                            onChange={(event) => {
                              if (event.target.checked) {
                                const first = detectionTargets[0];
                                setFieldValue('detection', {
                                  target: first.value,
                                  engine: 'zeroshot',
                                  captureOnly: true,
                                  framing: first.framing,
                                  useQuad: first.useQuad,
                                  noun: first.noun,
                                  decision: { threshold: 0.85, settleMs: 2500, stableFrames: 3 },
                                });
                                setFieldValue('cameraFacing', first.cameraFacing);
                              } else {
                                setFieldValue('detection', null);
                              }
                            }}
                          />
                        }
                        label="Auto-detect document type"
                        sx={{ display: 'block' }}
                      />
                      {formValues.detection && (
                        <Grid container spacing={1} sx={{ mt: 0.5 }}>
                          <Grid item xs={12}>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              label="Document type"
                              value={formValues.detection.target || detectionTargets[0].value}
                              onChange={(e) => {
                                // Everything a class needs comes WITH the class —
                                // guide box, quad on/off, which lens. Nothing here
                                // knows what a "face" or a "lock" is, which is why
                                // a new one needs no deploy.
                                const picked = optionFor(detectionTargets, e.target.value);
                                setFieldValue('detection', {
                                  ...formValues.detection,
                                  target: picked.value,
                                  framing: picked.framing,
                                  useQuad: picked.useQuad,
                                  noun: picked.noun,
                                });
                                setFieldValue('cameraFacing', picked.cameraFacing);
                              }}
                            >
                              {detectionTargets.map((t) => (
                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Confidence threshold (0-1)"
                              inputProps={{ step: 0.05, min: 0, max: 1 }}
                              value={formValues.detection.decision?.threshold ?? 0.85}
                              onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                setFieldValue('detection', {
                                  ...formValues.detection,
                                  decision: { ...(formValues.detection.decision || {}), threshold: isNaN(v) ? 0.85 : v },
                                });
                              }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              The camera guides the user to frame the document and only
                              captures a matching one. Non-matching documents are blocked.
                            </Typography>
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button type="submit" variant="contained" size="small">
                      Save
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik> 
      </Popover>
    </Box>
  );
}
